import { z } from 'zod';

// ============================================
// SCHEMA DE PRODUCTOS
// ============================================
export const productSchema = z.object({
  nombre: z.string().min(2, 'El nombre debe tener al menos 2 caracteres').max(67, 'El nombre no puede superar los 67 caracteres'),
  descripcion: z.string().optional(),
  marca: z.string().max(100).optional(),
  modelo: z.string().max(100).optional(),
  sku: z.string().max(100).optional(),
  codigoFabricante: z.string().max(100).optional(),
  codigoBarras: z.string().max(100).optional(),
  categoriaId: z.number().optional().nullable(),
  proveedorId: z.number().optional().nullable(),
  garantia: z.string().max(50).optional(),
  peso: z.number().min(0).optional(),
  dimensiones: z.string().max(100).optional(),
  color: z.string().max(50).optional(),
  precioCosto: z.number().min(0).optional().default(0),
  precio: z.number().min(0).optional().default(0),
  stock: z.number().min(0, 'El stock debe ser mayor o igual a 0'),
  stockMinimo: z.number().min(0, 'El stock mínimo debe ser mayor o igual a 0'),
  ganancia: z.number().min(0).optional(),
  ingresoBruto: z.number().min(0).optional(),
  costoAdicional: z.number().min(0).optional().default(0),
  comisiones: z.number().min(0).optional().default(0),
  observaciones: z.string().optional(),
  imagenes: z.string().optional(),
  imagenUrl: z.string().optional(),
  pdfUrl: z.string().optional(),
  documentoPdfUrl: z.string().optional(),
  precioFinal: z.number().optional().default(0),
  precioContado: z.number().optional().default(0),
  activo: z.boolean().optional().default(true),
});

export type ProductFormData = z.infer<typeof productSchema>;

// ============================================
// SCHEMA DE CLIENTES
// ============================================
export const clientSchema = z.object({
  nombre: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  cuil: z.string().optional(),
  telefono: z.string().optional(),
  email: z.string().email('Email inválido').optional(),
  direccion: z
    .string()
    .max(40, 'La dirección no puede superar los 40 caracteres')
    .optional()
    .or(z.literal('')),
  localidad: z.string().optional(),
  provincia: z.string().optional(),
  codigoPostal: z.string().optional(),
});

export type ClientFormData = z.infer<typeof clientSchema>;

// ============================================
// SCHEMA DE PROVEEDORES
// ============================================
export const supplierSchema = z.object({
  nombre: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  telefono: z.string().optional(),
  email: z.string().email('Email inválido').optional(),
  direccion: z
    .string()
    .max(40, 'La dirección no puede superar los 40 caracteres')
    .optional()
    .or(z.literal('')),
  localidad: z.string().optional(),
  provincia: z.string().optional(),
  codigoPostal: z.string().optional(),
  cuil: z.string().optional(),
});

export type SupplierFormData = z.infer<typeof supplierSchema>;

// ============================================
// SCHEMA DE CUOTAS
// ============================================
export const installmentSchema = z.object({
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteDni: z.string().min(7, 'El DNI debe tener al menos 7 dígitos'),
  clienteTelefono: z.string().optional(),
  clienteEmail: z.string().email('Email inválido').optional(),
  clienteDireccion: z.string().min(5, 'La dirección es requerida'),
  clienteLocalidad: z.string().min(3, 'La localidad es requerida'),
  clienteProvincia: z.string().min(3, 'La provincia es requerida'),
  clienteCp: z.string().optional(),
  productoId: z.number().optional(),
  productoNombre: z.string().min(2, 'El nombre del producto es requerido'),
  productoDescripcion: z.string().optional(),
  productoPrecioTotal: z.number().min(0, 'El precio total no puede ser negativo'),
  cantidadCuotas: z.number().int().min(1, 'Debe haber al menos 1 cuota'),
  fechaInicio: z.string().min(1, 'La fecha de inicio es requerida'),
  frecuenciaPago: z.enum(['semanal', 'quincenal', 'mensual']).default('mensual'),
  intereses: z.number().min(0).default(0),
  costoEnvio: z.number().min(0).default(0),
  anticipoMonto: z.number().min(0).default(0),
  observaciones: z.string().optional(),
});

export type InstallmentFormData = z.infer<typeof installmentSchema>;

// ============================================
// SCHEMA DE VENTAS
// ============================================
export const saleSchema = z.object({
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteDocumento: z.string().optional(),
  clienteTelefono: z.string().optional(),
  clienteDireccion: z.string().optional(),
  productos: z.array(z.object({
    productoId: z.number(),
    cantidad: z.number().min(1, 'La cantidad debe ser al menos 1'),
    precioUnitario: z.number().min(0, 'El precio no puede ser negativo'),
  })).min(1, 'Debe agregar al menos un producto'),
});

export type SaleFormData = z.infer<typeof saleSchema>;

// ============================================
// SCHEMA DE FACTURAS
// ============================================
export const invoiceSchema = z.object({
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteDocumento: z.string().optional(),
  clienteLocalidad: z.string().optional(),
  clienteDireccion: z.string().optional(),
  clienteProvincia: z.string().optional(),
  clienteCp: z.string().optional(),
  productos: z.array(z.object({
    nombre: z.string(),
    cantidad: z.number().min(1),
    precioUnitario: z.number().min(0),
  })).min(1, 'Debe agregar al menos un producto'),
  costoEnvio: z.number().min(0).optional().default(0),
  promocionPorcentaje: z.number().min(0).max(100).optional().default(0),
  observaciones: z.string().optional(),
});

export type InvoiceFormData = z.infer<typeof invoiceSchema>;

// ============================================
// SCHEMA DE REMITOS
// ============================================
export const shippingSchema = z.object({
  destinatarioNombre: z.string().min(2, 'El nombre del destinatario es requerido'),
  destinatarioDocumento: z.string().optional(),
  destinatarioTelefono: z.string().optional(),
  destinatarioDireccion: z.string().min(2, 'La dirección es requerida'),
  paqueteContenido: z.string().min(2, 'El contenido es requerido'),
  notasAdicionales: z.string().optional(),
});

export type ShippingFormData = z.infer<typeof shippingSchema>;

// ============================================
// SCHEMA DE MAYORISTAS
// ============================================
export const wholesaleSchema = z.object({
  nombreProducto: z.string().min(2, 'El nombre del producto es requerido'),
  tipo: z.string().optional(),
  precio: z.number().min(0, 'El precio debe ser mayor o igual a 0'),
  precioDescuento: z.number().min(0).optional(),
  precioVolumen: z.number().min(0).optional(),
  cantidadVolumen: z.number().min(0).optional(),
  stockActual: z.number().min(0).optional().default(0),
  stockMinimo: z.number().min(0).optional().default(0),
  proveedor: z.string().optional(),
  codigoBarras: z.string().optional(),
  notas: z.string().optional(),
});

export type WholesaleFormData = z.infer<typeof wholesaleSchema>;

// ============================================
// SCHEMA DE ENVÍOS
// ============================================
export const envioSchema = z.object({
  clienteId: z.number().optional().nullable(),
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteTelefono: z.string().optional().nullable(),
  clienteEmail: z.string().email('Email inválido').optional().nullable(),
  clienteDireccion: z.string().min(2, 'La dirección es requerida'),
  clienteDocumento: z.string().optional().nullable(),
  clienteLocalidad: z.string().optional().nullable(),
  productos: z.any(),
  paqueteContenido: z.string().optional().nullable(),
  observaciones: z.string().optional().nullable(),
  numeroSeguimiento: z.string().optional().nullable(),
  estado: z.string().optional().default('pendiente'),
  repartidorId: z.number().optional().nullable(),
  fechaAsignacion: z.string().optional().nullable(),
  fechaEntrega: z.string().optional().nullable(),
  fechaEnvio: z.string().optional().nullable(),
  pdfUrl: z.string().optional().nullable(),
  notasAdicionales: z.string().optional().nullable(),
});

export type EnvioFormData = z.infer<typeof envioSchema>;